import React, { useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Typography from 'Components/UI/Typography';
import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import PersonIcon from '@material-ui/icons/Person';
import {
  AccountCircleRounded,
  AssistantPhoto,
  CheckCircle,
  ChevronRight,
  ErrorRounded,
  FormatListBulleted,
  Help,
  Home,
  Timer,
} from '@material-ui/icons';
import Icon from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Icon';
import Notepad from 'Icons/Notepad';
import BarLineChart from 'Icons/BarLineChart';
import PiggyBank from 'Icons/PiggyBank';
import Hierarchy from 'Icons/Hierarchy';
import Scales from 'Icons/Scales';
import Tasks from 'Icons/Tasks';
import Profile from 'Icons/Profile';
import { noop } from 'Helpers/PropTypes';
import styles from 'Views/Onboarding/Components/Navigation/navigationCardStyles';
import { CircularProgress } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';

// We should be sending the icon with the navigation item instead of storing this map
const iconForModuleMap = {
  annualReviewWelcome: Notepad,
  accountOpeningKYC: PersonIcon,
  annualReviewPersonalInformation: PersonIcon,
  annualInvestmentReviewInvestmentObjectives: BarLineChart,
  annualReviewIncome: PiggyBank,
  annualReviewRiskTolerance: Scales,
  annualReviewComplete: AssistantPhoto,
  investmentProfileWelcome: Notepad,
  investmentProfilePersonalInformation: PersonIcon,
  employmentAndOther: PiggyBank,
  trustedContactInformation: PersonIcon,
  accountOpeningWelcome: Home,
  accountOpeningBeneficiaryList: Hierarchy,
  accountProposal: BarLineChart,
  exit: Home,
  investmentProfileFamilyMemberList: PersonIcon,
  accountOpeningAccountOwnerList: PersonIcon,
  plannerAccountApplicationHousehold: PersonIcon,
  investmentProfilePrimaryClientInformation: PersonIcon,
  investmentProfileSecondaryClientInformation: PersonIcon,
  plannerAccountApplicationAccountType: AssistantPhoto,
  plannerAccountApplicationAccountOwners: Profile,
  plannerAccountApplicationModelAssignment: Scales,
  plannerAccountApplicationFundingInstructions: Tasks,
  accountOpeningTrustInformation: Tasks,
  accountOpeningPrimaryApplicantInvalidProfileInformation: ErrorRounded,
  accountOpeningSecondaryApplicantInvalidProfileInformation: ErrorRounded,
  accountOpeningSpecialAccountInformation: FormatListBulleted,
  accountOpeningFewMoreThings: AccountCircleRounded,
  accountOpeningAgreements: Notepad,
};

const NavigationCard = ({
  className,
  classes,
  toggleDrawer,
  onClick,
  compact,
  id,
  isComplete,
  isInProgress,
  isNextUp,
  isDisabled,
  primaryText,
  secondaryText,
  estimatedDuration,
}) => {
  const isWorkingRef = useRef(false);
  let svgIconFillColor = isNextUp ? 'primary' : 'mediumGray';
  if (!onClick) {
    svgIconFillColor = 'primaryGray2';
  }

  const handleClick = async (evt) => {
    if (!onClick) return;

    if (isWorkingRef.current) {
      return;
    }

    isWorkingRef.current = true;

    try {
      await onClick(evt);
    } catch (err) {
      throw err; // Don't get in the way here.
    }

    toggleDrawer();

    isWorkingRef.current = false;
  };

  return (
    <Paper
      className={classNames(classes.root, {
        [classes.nextUpPaper]: isNextUp,
        [classes.disabledPaper]: isDisabled,
        [classes.clickable]: onClick,
        [classes.compactPaper]: compact,
        [classes.expandedPaper]: !compact,
      }, className)}
      onClick={handleClick}
    >
      { id !== 'exit' || compact
        ? <Icon
          icon={iconForModuleMap[id] || Help} // help is the default if no icon is found for the navigation item
          className={classNames(classes.icon, {
            [classes.iconNextUp]: isNextUp,
            [classes.iconDisabled]: isDisabled,
            [classes.compactIcon]: compact,
          })}
          fill={svgIconFillColor}
          test={`nav-card-icon-${id}`}
        /> : null}
      <div
        className={classNames(classes.content, {
          [classes.expanded]: !compact,
          [classes.compact]: compact,
        })}
      >
        <div className={classes.textContent}>
          <Typography
            className={classes.primaryText}
            color="primary"
            variant="h6"
            test={`nav-card-title-${id}`}
          >
            {primaryText}
          </Typography>
          <Typography
            className={classes.secondaryText}
            weight={'medium'}
            color="accentBlueMuted2"
            variant="subtitle2"
          >
            {secondaryText}
          </Typography>
        </div>

        {isInProgress && !isWorkingRef.current ? (
          <Typography
            color="primaryGray2"
            variant="h6"
            className={classes.inProgress}
          >
            {'In Progress'}
          </Typography>
        ) : null}

        {isComplete && !isWorkingRef.current ? (
          <CheckCircle
            color="secondary"
            test="nav-card-complete"
            className={classes.check}
          />
        ) : null}

        {isWorkingRef.current ? (
          <CircularProgress size={20} className={classes.loadingIndicator} />
        ) : null}

        {(!isComplete && !isInProgress && estimatedDuration && !isWorkingRef.current) ? (
          <Typography
            variant="h6"
            color="primaryGray2"
            className={classes.timer}
          >
            <Timer fontSize="small" className={classes.timerIcon} />
            {estimatedDuration}
          </Typography>
        ) : null}

        {onClick ? (
          <ChevronRight
            className={classes.chevron}
            test={`nav-card-next-link-${id}`}
          />
        ) : (
          <span
            className={classes.spacer}
            test={`nav-card-disabled-${id}`}
          />
        )}
      </div>
    </Paper>
  );
};

NavigationCard.defaultProps = {
  className: '',
  compact: false,
  toggleDrawer: noop,
  onClick: undefined,
  isComplete: false,
  isInProgress: false,
  isNextUp: false,
  isDisabled: false,
  primaryText: undefined,
  secondaryText: undefined,
  estimatedDuration: undefined,
};

NavigationCard.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  toggleDrawer: PropTypes.func,
  compact: PropTypes.bool,
  onClick: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isComplete: PropTypes.bool,
  isInProgress: PropTypes.bool,
  isNextUp: PropTypes.bool,
  isDisabled: PropTypes.bool,
  primaryText: PropTypes.string,
  secondaryText: PropTypes.string,
  estimatedDuration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default withStyles(styles)(NavigationCard);
